/**
 * InsufficientAccessSheet — bottom sheet shown on 402 from any POST (Phase 5.1).
 *
 * Triggered by axios response interceptor in api/client.js → emits
 * `app:insufficient-access` CustomEvent → App.jsx listens globally and opens
 * this sheet. Also used directly by PhotoToVideoSheet (FREE + 1080p paywall)
 * for plan-restriction messaging.
 *
 * Props:
 *  - open     (bool)
 *  - message? (string)  — custom copy; falls back to generic
 *  - onClose  (fn)
 *  - onCta    (fn)      — primary CTA → typically navigate('/plans')
 */
import { UIButton } from './primitives';
import { useTranslation } from 'react-i18next';

export default function InsufficientAccessSheet({ open, message, onClose, onCta }) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="insufficient-sheet"
      className="fixed inset-0 z-[70] flex items-end justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/[0.64]" />
      <div
        className="relative w-full max-w-md rounded-t-3xl bg-popup-gradient p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}
      >
        <div aria-hidden className="self-center w-10 h-1 rounded-full bg-text-tertiary/40" />
        <h2 className="font-sans font-bold text-h1 text-text-primary text-center">
          {t('insufficient.title')}
        </h2>
        <p className="font-sans text-plan text-text-secondary text-center">
          {message || t('insufficient.subtitle')}
        </p>
        <UIButton
          type="primary"
          size="md"
          onClick={onCta}
          className="w-full"
          data-testid="insufficient-cta"
        >
          {t('insufficient.cta')}
        </UIButton>
        <UIButton
          type="tertiary"
          size="md"
          onClick={onClose}
          className="w-full"
          data-testid="insufficient-close"
        >
          {t('insufficient.later')}
        </UIButton>
      </div>
    </div>
  );
}
